import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { SubNavComponent } from '../../../../components/sub-nav/sub-nav.component';
import { SubNavTabDirective } from '../../../../components/sub-nav/sub-nav-tab.directive';
import { BreadcrumbLabelDirective } from '../../../../directives/breadcrumb-label.directive';
import { ApiService } from '../../../../services/api.services';
import { PropertyAboutComponent } from '../../../property/detail/property-about/property-about.component';
import Property = Components.Schemas.Location;

/**
 * Company-shell Single Property page (ITE-12009).
 * Details reuse PropertyAbout; Devices/Clients are stubs until ITE-13208.
 */
@Component({
	selector: 'app-company-property-detail',
	templateUrl: './company-property-detail.component.html',
	styleUrl: './company-property-detail.component.scss',
	imports: [
		SubNavComponent,
		SubNavTabDirective,
		PropertyAboutComponent,
		BreadcrumbLabelDirective,
	],
})
export class CompanyPropertyDetailComponent implements OnInit {
	companyId = '';
	propertyId = '';
	property?: Property;
	canViewClients = false;
	breadcrumbLabel = '';

	constructor(
		private readonly route: ActivatedRoute,
		private readonly router: Router,
		private readonly apiService: ApiService
	) {
		this.companyId =
			this.route.snapshot.params.companyId ?? this.parentCompanyId();
		this.propertyId =
			this.route.snapshot.params.propertyId ?? this.route.snapshot.params.id;
	}

	async ngOnInit(): Promise<void> {
		this.companyId =
			this.route.snapshot.params.companyId ?? this.parentCompanyId();
		this.propertyId =
			this.route.snapshot.params.propertyId ?? this.route.snapshot.params.id;
		await this.loadProperty();
		await this.checkPropertyHasClients();
	}

	get propertyName(): string {
		return this.property?.name ?? '';
	}

	onPropertiesClick(): void {
		// Drop the nested property route so the company Properties list shows again.
		void this.router.navigate(['/company', this.companyId, 'detail'], {
			queryParams: { propertyTab: null },
			queryParamsHandling: 'merge',
		});
	}

	private parentCompanyId(): string {
		return this.route.parent?.snapshot.params.id ?? '';
	}

	private async loadProperty(): Promise<void> {
		this.property = await this.apiService.getProperty(this.propertyId);
		this.breadcrumbLabel = this.property?.name ?? '';
	}

	/**
	 * Clients tab: same gate as /property-details, evaluated for the route company
	 * (care company or property already has clients).
	 */
	private async checkPropertyHasClients(): Promise<void> {
		try {
			let isCareCompany = false;
			if (this.companyId) {
				const companyData = await this.apiService.getCompanyById(
					this.companyId
				);
				isCareCompany = companyData?.company?.companyType === 'care';
			}

			const clients = await this.apiService.getClientsByLocationId(
				this.propertyId,
				[
					{ param: 'rowCount', value: '1' },
					{ param: 'rowIndex', value: '0' },
				]
			);

			const hasClients = !!clients && clients.length > 0;
			this.canViewClients = isCareCompany || hasClients;
		} catch {
			this.canViewClients = false;
		}
	}
}
